import { AxiosResponse } from 'axios'
import { apiPaths } from 'configs/apiConfigs'
import { OperatorTypeEnum } from 'types/tableTypes'
import { encodeUri } from 'utils'
import { httpClaims } from 'utils/http'

import moment from 'moment'
import { BatchProcessingStatus, BatchType, IbatchArchive, IGetBatchResponse, ValidationDownloadType } from './types'

const buildBatchQuery = (status?: Array<BatchProcessingStatus>, batchType?: Array<BatchType>, limit?: string) => {
  const query = []
  limit ? query.push(`limit=${limit}`) : query.push(`limit=0`)
  if (status) {
    query.push(
      `Filter=${encodeUri({
        path: 'processingStatus',
        operator: OperatorTypeEnum.in,
        valueList: status,
      })}`,
    )
  }
  query.push(
    `Filter=${encodeUri({
      path: 'archived',
      operator: '=',
      value: 'false',
    })}`,
  )

  if (batchType) {
    query.push(
      `Filter=${encodeUri({
        path: 'batchType',
        operator: OperatorTypeEnum.in,
        valueList: batchType,
      })}`,
    )
  }

  query.push(
    `Sort=${encodeUri({
      path: 'lastUpdatedTime',
      descending: true,
    })}`,
  )
  return query.length ? `?${query.join('&')}` : ''
}

export const getBatchCount = async (): Promise<IGetBatchResponse> => {
  const dataQuery = buildBatchQuery([
    BatchProcessingStatus.AutoCheck_In_Queue,
    BatchProcessingStatus.Ready_for_Processing,
    BatchProcessingStatus.Cancelling,
    BatchProcessingStatus.AutoCheck_In_Progress,
  ])
  const { data } = await httpClaims.get(apiPaths.batches(dataQuery))
  return data
}

export const getBatchByBatchId = async (batchId: string): Promise<IGetBatchResponse> => {
  const query = []
  query.push(`limit=1`)
  query.push(
    `Filter=${encodeUri({
      path: 'batchId',
      operator: '=',
      value: batchId,
    })}`,
  )
  query.push(
    `Filter=${encodeUri({
      path: 'archived',
      operator: '=',
      value: 'false',
    })}`,
  )
  const dataQuery = query.length ? `?${query.join('&')}` : ''
  const { data } = await httpClaims.get(apiPaths.batches(dataQuery))
  return data
}

export const getAwaitingEOBBatchCount = async (): Promise<IGetBatchResponse> => {
  const dataQuery = buildBatchQuery([BatchProcessingStatus.Awaiting_EOB])
  const { data } = await httpClaims.get(apiPaths.batches(dataQuery))
  return data
}

export const getBatchProcessedCount = async (): Promise<IGetBatchResponse> => {
  const dataQuery = buildBatchQuery(
    [BatchProcessingStatus.Complete, BatchProcessingStatus.Cancelled, BatchProcessingStatus.FailedToComplete],
    [BatchType.Claim_Autocheck, BatchType.Claim_Autocheck_EOB, BatchType.Claim_Import, BatchType.Claim_ASC],
  )
  const { data } = await httpClaims.get(apiPaths.batches(dataQuery))
  return data
}

export const getBatchPendingCount = async (): Promise<IGetBatchResponse> => {
  const dataQuery = buildBatchQuery(
    [BatchProcessingStatus.Pending_Approval, BatchProcessingStatus.Uploading_Claims],
    [BatchType.Claim_Autocheck, BatchType.Claim_Autocheck_EOB, BatchType.Claim_Import, BatchType.Claim_ASC],
  )
  const { data } = await httpClaims.get(apiPaths.batches(dataQuery))
  return data
}

export const GetClaimsFiles = async (): Promise<IGetBatchResponse> => {
  const dataQuery = buildBatchQuery(
    [BatchProcessingStatus.Pending_Approval, BatchProcessingStatus.Uploading_Claims],
    [BatchType.Claim_Autocheck, BatchType.Claim_Import, BatchType.Claim_ASC],
    '20',
  )
  const { data } = await httpClaims.get(apiPaths.batches(dataQuery))
  return data
}

export const getBatch = async (): Promise<IGetBatchResponse> => {
  const dataQuery = buildBatchQuery(
    [
      BatchProcessingStatus.AutoCheck_In_Queue,
      BatchProcessingStatus.Ready_for_Processing,
      BatchProcessingStatus.Cancelling,
      BatchProcessingStatus.AutoCheck_In_Progress,
    ],
    [],
    '20',
  )
  const { data } = await httpClaims.get(apiPaths.batches(dataQuery))
  return data
}

export const getBatchProcessed = async (): Promise<IGetBatchResponse> => {
  const dataQuery = buildBatchQuery(
    [BatchProcessingStatus.Complete, BatchProcessingStatus.Cancelled, BatchProcessingStatus.FailedToComplete],
    [BatchType.Claim_Autocheck, BatchType.Claim_Autocheck_EOB, BatchType.Claim_Import, BatchType.Claim_ASC],
    '20',
  )
  const { data } = await httpClaims.get(apiPaths.batches(dataQuery))
  return data
}

export const getDownloadBatch = async (): Promise<any> => {
  const query = []
  const currentUser = localStorage.getItem('currentUser')
  const user = currentUser ? JSON.parse(currentUser) : null
  query.push(
    `Filter=${encodeUri({
      path: 'batchType',
      operator: OperatorTypeEnum.in,
      valueList: [BatchType.Claim_Export],
    })}`,
  )
  query.push(
    `Filter=${encodeUri({
      path: 'created',
      operator: '>',
      value: moment.utc().subtract(24, 'h').format('YYYY-MM-DD HH:mm:ss'),
    })}`,
  )
  query.push(
    `Filter=${encodeUri({
      path: 'userId',
      operator: '=',
      value: user?.id,
    })}`,
  )
  query.push('limit=1000')
  const dataQuery = query.length ? `?${query.join('&')}` : ''
  const { data } = await httpClaims.get(apiPaths.batchesNotification(dataQuery))
  return data
}

export const postbatchClaimsDownload = async (query: any): Promise<any> => {
  const { data } = await httpClaims.post(apiPaths.batchClaimFilter(query))
  return data
}

export const batchCallinProgress = async (batchId: string): Promise<AxiosResponse> => {
  const query = []
  query.push(
    `Filter=${encodeUri({
      path: 'batchId',
      operator: '=',
      value: batchId,
    })}`,
  )

  const dataQuery = query.length ? `?${query.join('&')}` : ''
  const { data } = await httpClaims.get(apiPaths.batches(dataQuery))
  return data
}

export const getUnmatchedEobs = async (dataQuery: any): Promise<any> => {
  const { data } = await httpClaims.get(apiPaths.unmatchedEobs(dataQuery))
  return data
}

export const getUnSupportedFaxes = async (dataQuery: any): Promise<any> => {
  const { data } = await httpClaims.get(apiPaths.unSupportedFaxes(dataQuery))
  return data
}

export const downloadUnmatchedEOB = async (query: any): Promise<any> => {
  const { data } = await httpClaims.get(apiPaths.unmatchedEobDownload(query), {
    headers: { accept: 'application/zip' },
    responseType: 'blob',
  })
  return data
}

export const downloadUnSupportedFax = async (query: any): Promise<any> => {
  const { data } = await httpClaims.get(apiPaths.unSupportedFaxDownload(query), {
    headers: { accept: 'application/zip' },
    responseType: 'blob',
  })
  return data
}

export const downloadUnmatchedServiceLineItems = async (query: string): Promise<any> => {
  const { data } = await httpClaims.get(apiPaths.unmatchedServiceLineItemDownload(query), {
    headers: { accept: 'text/csv' },
    responseType: 'blob',
  })
  return data
}

export const getBatchResults = async (batchId: string): Promise<any> => {
  const { data } = await httpClaims.get(apiPaths.batchResults(batchId))
  return data
}

export const stopProcessing = async (batchId: string): Promise<any> => {
  const { data } = await httpClaims.patch(apiPaths.batchCancel(batchId))
  return data
}

export const getBatchStatus = async (): Promise<any> => {
  const { data } = await httpClaims.get(apiPaths.checkBatchStatus())
  return data
}

export const archiveClaims = async (batchDetails: IbatchArchive): Promise<any> => {
  const { data } = await httpClaims.patch(apiPaths.batchArchive(), batchDetails)
  return data
}

export const upgradeBatch = async (batchId: string, UpgradeBatch: BatchType): Promise<any> => {
  const UpgradeBatchTypeData = {
    newBatchType: UpgradeBatch,
  }

  const data = await httpClaims.patch(apiPaths.batchUpgrade(batchId), UpgradeBatchTypeData)
  return data
}

export const upgradeBatchwithAutoCheck = async (batchId: string, UpgradeBatch: BatchType): Promise<any> => {
  const UpgradeBatchTypeData = {
    newBatchType: UpgradeBatch,
  }

  const data = await httpClaims.patch(apiPaths.batchUpgradewithAutoCheck(batchId), UpgradeBatchTypeData)
  return data
}

export const exportBatch = async (batchId: string): Promise<any> => {
  const { data } = await httpClaims.get(apiPaths.batchExport(batchId), {
    headers: { accept: 'text/csv' },
    responseType: 'blob',
  })
  return data
}

export const exportBatch2 = async (batchId: string, exportType: string): Promise<any> => {
  const data = await httpClaims.get(apiPaths.batchExport2(batchId, exportType), {
    headers: { accept: 'text/csv' },
    responseType: 'blob',
  })
  return data
}

export const exportLineItem = async (batchId: string): Promise<any> => {
  const { data } = await httpClaims.get(apiPaths.batchLineItemExport(batchId), {
    headers: { accept: 'text/csv' },
    responseType: 'blob',
  })
  return data
}

export const exportBatchClaims = async (batchId: string, type: ValidationDownloadType): Promise<any> => {
  const data = await httpClaims.get(apiPaths.batchClaimsExport(batchId, type), {
    headers: { accept: 'text/csv' },
    responseType: 'blob',
  })
  return data
}

export const exportBatchClaimsZip = async (batchId: string): Promise<any> => {
  const data = await httpClaims.get(apiPaths.batchEOBExport(batchId), {
    headers: { accept: 'application/zip' },
    responseType: 'blob',
  })
  return data
}

export const getBatchErrorsByBatchId = async (batchId: string): Promise<any> => {
  const data = await httpClaims.get(apiPaths.batchErrors(batchId))
  return data
}

export const batchUpload = async (file: File, type: BatchType, repeatedCalls: boolean): Promise<AxiosResponse> => {
  const formData = new FormData()
  formData.append('file', file)

  return httpClaims.post(apiPaths.uploadBatch(type, repeatedCalls), formData, {
    headers: { 'Content-Type': 'multipart/form-data', Accept: 'text/plain' },
  })
}

export const getCustomerBatchCount = async (): Promise<any> => {
  const data = await httpClaims.get(apiPaths.customerBatchCount())
  return data.data
}

export const getBatchEOBPartDetails = async (batchId: string, query: string): Promise<any> => {
  const data = await httpClaims.get(apiPaths.batchEOBPartDetails(batchId, query))
  return data
}

export const getPreSignedUrl = async (url: string, query: string): Promise<any> => {
  const data = await httpClaims.get(apiPaths.getPreSignedUrl(url, query))
  return data
}
